import { Button } from "antd";
import { useTranslation } from "react-i18next";

import { JsonViewer, StatusBadge } from "sandbox/features/shared";
import { AMP_MASK_CLASS } from "shell/api/amplitudePrivacy";

import s from "./ResponsePanel.module.css";

interface ResponseState {
  body: unknown;
  bytes: number;
  durationMs: number;
  raw: string;
  status: number;
}

interface ResponsePanelProps {
  response: ResponseState | null;
  sending: boolean;
  activeFormat: "pretty" | "raw";
  onFormatChange: (format: "pretty" | "raw") => void;
}

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} kB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

export const ResponsePanel = ({
  response,
  sending,
  activeFormat,
  onFormatChange,
}: ResponsePanelProps) => {
  const { t } = useTranslation("sandbox");

  return (
    <div className={s.panel}>
      <div className={s.header}>
        <span className={s.title}>{t("components.requestPane.response")}</span>
        {response && (
          <div className={s.meta}>
            <StatusBadge status={response.status} />
            <span className={s.metaText}>
              {response.durationMs}ms / {formatSize(response.bytes)}
            </span>
          </div>
        )}
        <div className={s.formatToggle}>
          <Button
            className={activeFormat === "pretty" ? s.formatActive : s.format}
            onClick={() => onFormatChange("pretty")}
          >
            {t("labels.pretty")}
          </Button>
          <Button
            className={activeFormat === "raw" ? s.formatActive : s.format}
            onClick={() => onFormatChange("raw")}
          >
            {t("labels.raw")}
          </Button>
        </div>
      </div>

      <div className={`${s.body} ${AMP_MASK_CLASS}`}>
        {sending ? (
          <p className={s.empty}>
            {t("components.requestPane.sendingRequest")}
          </p>
        ) : !response ? (
          <p className={s.empty}>{t("components.requestPane.emptyResponse")}</p>
        ) : activeFormat === "pretty" ? (
          <JsonViewer data={response.body} />
        ) : (
          <pre className={s.rawBody}>{response.raw}</pre>
        )}
      </div>
    </div>
  );
};
